import type {
  Advertiser,
  AdvertiserAddress,
  AdvertiserEntity,
  AdvertiserEntityInput,
  AdvertiserEntityType,
  AdvertiserPoliticalBudgetInput,
} from '@/services/advertisers/advertisersApi'

/**
 * Form state for the political budgets and entities an advertiser declares
 * itself — the older way of political advertising, where everything lives on
 * the advertiser rather than on a linked political campaign.
 */

export interface TopicBudgetDraft {
  /** Stable row key, so removing a row does not shift the inputs below it. */
  key: string
  name: string
  amount: string
}

export interface PoliticalBudgetDraft {
  key: string
  electionId: string
  /** One currency covers the total and every topic of the election. */
  currency: string
  totalBudget: string
  topics: TopicBudgetDraft[]
}

export interface EntityDraft {
  /** The advertiser plays this role itself, so no separate entity is sent. */
  same: boolean
  legalEntity: string
  legalName: string
  contactEmail: string
  url: string
  vatId: string
  address: AdvertiserAddress
}

export const ENTITY_TYPES: AdvertiserEntityType[] = ['CONTROLLING', 'PAYING']

const EMPTY_ADDRESS: AdvertiserAddress = {
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postCode: '',
  country: '',
}

export function newRowKey(): string {
  return crypto.randomUUID()
}

export function newTopicDraft(): TopicBudgetDraft {
  return { key: newRowKey(), name: '', amount: '' }
}

export function newBudgetDraft(currency: string): PoliticalBudgetDraft {
  return { key: newRowKey(), electionId: '', currency, totalBudget: '', topics: [] }
}

export function budgetDrafts(advertiser: Advertiser | null, fallbackCurrency: string): PoliticalBudgetDraft[] {
  return (advertiser?.politicalBudgets ?? []).map((budget) => ({
    key: newRowKey(),
    electionId: budget.electionId,
    currency:
      budget.totalBudget?.currency ||
      Object.values(budget.topicBudgets).find(Boolean)?.currency ||
      fallbackCurrency,
    totalBudget: budget.totalBudget ? String(budget.totalBudget.amount) : '',
    topics: budget.topics.map((name) => {
      const money = budget.topicBudgets[name]
      return { key: newRowKey(), name, amount: money ? String(money.amount) : '' }
    }),
  }))
}

function entityHasValue(entity: AdvertiserEntity): boolean {
  return (
    [entity.legalEntity, entity.legalName, entity.contactEmail, entity.url, entity.vatId].some(Boolean) ||
    Object.values(entity.address).some(Boolean)
  )
}

/** An entity with nothing filled in means the advertiser plays that role itself. */
export function entityDraft(advertiser: Advertiser | null, type: AdvertiserEntityType): EntityDraft {
  const entity = advertiser?.entities.find((e) => e.type === type)
  if (!entity || !entityHasValue(entity)) {
    return {
      same: true,
      legalEntity: '',
      legalName: '',
      contactEmail: '',
      url: '',
      vatId: '',
      address: { ...EMPTY_ADDRESS },
    }
  }
  return { same: false, ...entity, address: { ...entity.address } }
}

export function budgetsInput(drafts: PoliticalBudgetDraft[]): AdvertiserPoliticalBudgetInput[] {
  return drafts.map((draft) => ({
    election: { id: draft.electionId },
    totalBudget: { amount: Number(draft.totalBudget), currency: draft.currency },
    campaigns: Object.fromEntries(
      draft.topics.map((topic) => [
        topic.name.trim(),
        { amount: Number(topic.amount), currency: draft.currency },
      ])
    ),
  }))
}

export function entitiesInput(drafts: Record<AdvertiserEntityType, EntityDraft>): AdvertiserEntityInput[] {
  return ENTITY_TYPES.filter((type) => !drafts[type].same).map((type) => {
    const { same: _same, legalEntity, ...rest } = drafts[type]
    // The API rejects an empty legal entity, so leave it out rather than send ''.
    return { type, ...rest, ...(legalEntity ? { legalEntity } : {}) }
  })
}

export interface BudgetIssues {
  election?: 'required' | 'duplicate'
  totalBudget?: 'required'
  /** Per topic row key. */
  topics: Record<string, { name?: 'required' | 'duplicate'; amount?: 'required' }>
}

function isAmount(value: string): boolean {
  return value.trim() !== '' && Number.isFinite(Number(value))
}

/**
 * What stops each budget from saving, keyed by row. The API keys topic budgets
 * by name, so a topic name may appear only once per election, and each
 * election only once per advertiser.
 */
export function budgetIssues(drafts: PoliticalBudgetDraft[]): Record<string, BudgetIssues> {
  const issues: Record<string, BudgetIssues> = {}
  const seenElections = new Set<string>()
  for (const draft of drafts) {
    const row: BudgetIssues = { topics: {} }
    if (!draft.electionId) row.election = 'required'
    else if (seenElections.has(draft.electionId)) row.election = 'duplicate'
    seenElections.add(draft.electionId)
    if (!isAmount(draft.totalBudget)) row.totalBudget = 'required'

    const seenTopics = new Set<string>()
    for (const topic of draft.topics) {
      const name = topic.name.trim()
      const topicIssues: BudgetIssues['topics'][string] = {}
      if (!name) topicIssues.name = 'required'
      else if (seenTopics.has(name)) topicIssues.name = 'duplicate'
      seenTopics.add(name)
      if (!isAmount(topic.amount)) topicIssues.amount = 'required'
      if (topicIssues.name || topicIssues.amount) row.topics[topic.key] = topicIssues
    }

    if (row.election || row.totalBudget || Object.keys(row.topics).length) issues[draft.key] = row
  }
  return issues
}
